import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, Layers, MapPin, Search } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import tradieTeamPhoneCall from "@/assets/tradie-team-phone-call.png.asset.json";

const SUPPORT_CARDS = [
  {
    icon: Search,
    title: "Built around what you need",
    body: "We find the biggest gaps first, then recommend only the work most likely to make a difference. We’ll also tell you what you don’t need yet.",
  },
  {
    icon: BarChart3,
    title: "Clear reporting that makes sense",
    body: "See your calls, enquiries, Google visibility and reviews in one straightforward monthly update.",
  },
  {
    icon: MapPin,
    title: "Australian owned and locally focused",
    body: "Built for trade and service businesses competing for work in specific suburbs and service areas.",
  },
];

export function BentoSection() {
  return (
    <section className="section-padding bg-muted relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-40" />
      <div className="container-wide relative z-10">
        {/* Section header */}
        <Reveal className="max-w-2xl mb-12 sm:mb-14 lg:mb-16">
          <span className="eyebrow-rule mb-4">Why FullScale</span>
          <h2 className="heading-section text-foreground mt-4 mb-5 text-balance">
            Built for businesses that depend on{" "}
            <span className="heading-accent">local demand</span>
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-[1.65] prose-cap">
            We help Australian trade and service businesses improve the parts of
            their marketing that directly affect calls, enquiries and booked
            jobs.
          </p>
        </Reveal>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_0.55fr] gap-5 sm:gap-6">
          {/* Left: feature card */}
          <Reveal className="h-full">
            <article className="h-full flex flex-col rounded-2xl border border-border/60 bg-card shadow-card overflow-hidden text-left">
              <img
                src={tradieTeamPhoneCall.url}
                alt="Australian trade business owner taking a customer call while his team loads a white work ute"
                width={1280}
                height={640}
                loading="lazy"
                className="w-full h-[220px] sm:h-[280px] lg:h-[320px] object-cover"
              />
              <div className="flex-1 p-6 sm:p-7">
                <Layers className="w-6 h-6 text-primary mb-4" strokeWidth={1.5} />
                <h3 className="text-lg sm:text-xl font-bold text-foreground mb-2.5">
                  One connected system for local growth
                </h3>
                <p className="text-[0.9375rem] sm:text-base text-muted-foreground leading-[1.7] mb-5">
                  Your website, Google presence, reviews and lead follow-up work
                  together—so more local searches turn into genuine calls and
                  quote requests.
                </p>
                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:gap-3 transition-all"
                >
                  See how the system works
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </article>
          </Reveal>

          {/* Right: stacked cards */}
          <div className="flex flex-col gap-5 sm:gap-6">
            {SUPPORT_CARDS.map((card, i) => (
              <Reveal key={card.title} delay={0.08 + i * 0.06} className="flex-1">
                <article className="h-full rounded-2xl border border-border/60 bg-card shadow-card p-6 sm:p-7 text-left flex flex-col">
                  <card.icon
                    className="w-6 h-6 text-primary mb-4"
                    strokeWidth={1.5}
                  />
                  <h3 className="text-base sm:text-lg font-bold text-foreground mb-2.5">
                    {card.title}
                  </h3>
                  <p className="text-[0.9375rem] sm:text-base text-muted-foreground leading-[1.7]">
                    {card.body}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
